/* eslint-disable no-nested-ternary */
import { useMemo } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { useTranslation } from 'react-i18next';
import Modal from '../../../modules/Modal';

interface Props {
  /** モーダル表示・非表示状態。 */
  isOpen: boolean;

  /** モーダル表示・非表示状態設定関数。 */
  setIsOpen: (isOpen: boolean) => void;

  /** ダイアログタイトル */
  titleText: string | null;

  /** 言語ID */
  langugeId: number;

  /** 変更前テキスト */
  oldText: string | object | undefined;

  /** 変更後テキスト */
  newText: string | object | undefined;

  /** 変更前テキストタイトル(空の場合非表示) */
  oldTitle: string;

  /** 変更後テキストタイトル(空の場合非表示) */
  newTitle: string;

  /** true:xml */
  isXml: boolean;
}

// PS 編集画面で使用。
/** PS比較ダイアログ。 */
function PsDiffViewerDialog({
  isOpen,
  setIsOpen,
  titleText,
  langugeId,
  oldText,
  newText,
  oldTitle,
  newTitle,
  isXml,
}: Props) {
  // i18n 用。
  const { t, i18n } = useTranslation();

  const xmlFormat = (parent: Element, indent: string, depth: number) => {
    const depthL = depth + 1;
    const indentStr1 = indent.repeat(depthL);
    let indentStr: string = depthL === 0 ? '' : `\n${indentStr1}`;
    let element = parent.firstElementChild;
    let childCount = 0;
    while (element) {
      element.before(indentStr);
      const child = element.firstElementChild;
      if (child) {
        xmlFormat(element, indent, depthL); // 再帰

        // 終了タグの前のインデントを入れる。
        element.append(indentStr);
      }
      element = element.nextElementSibling;
      childCount += 1;
      if (indentStr === '' && childCount > 0) {
        indentStr = '\n';
      }
    }
  };

  const changeText = (value: string | object | undefined, isStrXml: boolean) => {
    if (isStrXml && typeof value === 'string') {
      if (value !== '') {
        const parser = new DOMParser();
        const dom = parser.parseFromString(value, 'text/html');
        const bodyElement = dom.body;
        xmlFormat(bodyElement, '  ', -1);
        bodyElement.append('\n');
        return bodyElement.innerHTML;
      }
      return value ?? '';
    }
    return value ?? '';
  };

  /** Diffダイアログの言語タイトル */
  const getSubTitle = useMemo(() => {
    if (langugeId === -1) return '';
    if (langugeId === 0) return ` (${t('日本語')})`;
    if (langugeId === 1) return ` (${t('米語')})`;
    if (langugeId === 2) return ` (${t('英語')})`;
    return '';
  }, [langugeId, t]);

  const oldValue: string | object | undefined = changeText(oldText, isXml);
  const newValue: string | object | undefined = changeText(newText, isXml);

  /** 折り返しスクロール無し */
  const newStyles = {
    line: {
      wordBreak: 'break-word',
    },
  };

  return (
    <Modal
      active={isOpen}
      additionalClassName="popupfrfree"
      displayCloseButton
      onClose={() => {
        setIsOpen(false);
      }}
      modalClassName="modal-body-psdiff"
    >
      <div>
        <p>
          <strong>
            {titleText}
            {getSubTitle}
          </strong>
        </p>
        <ReactDiffViewer
          oldValue={oldValue}
          newValue={newValue}
          splitView
          leftTitle={oldTitle}
          rightTitle={newTitle}
          extraLinesSurroundingDiff={1} // Show 1 extra line around the diff
          showDiffOnly={false}
          styles={newStyles}
        />
        <p>react-diff-viewer-continuedコンポーネントを使用</p>
      </div>
    </Modal>
  );
}

export default PsDiffViewerDialog;
